import React, { useState } from 'react';

const CloseButtonIcon = () => (
  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CopyIcon = () => (
  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const SuccessIcon = () => (
  <svg className="h-10 w-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FailureIcon = () => (
  <svg className="h-10 w-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const InfoIcon = () => (
  <svg className="h-10 w-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const VIBE_CONFIG = {
  success: {
    icon: SuccessIcon,
    border: 'border-green-200',
    iconColor: 'text-green-500',
    titleColor: 'text-green-800',
    msgBg: 'bg-green-50',
    msgBorder: 'border-green-200',
    msgText: 'text-green-700',
    accentRing: 'focus:ring-green-500',
    topStripe: 'bg-gradient-to-r from-green-400 to-emerald-500',
  },
  failure: {
    icon: FailureIcon,
    border: 'border-red-200',
    iconColor: 'text-red-500',
    titleColor: 'text-red-800',
    msgBg: 'bg-red-50',
    msgBorder: 'border-red-200',
    msgText: 'text-red-700',
    accentRing: 'focus:ring-red-500',
    topStripe: 'bg-gradient-to-r from-red-400 to-rose-500',
  },
  neutral: {
    icon: InfoIcon,
    border: 'border-gray-200',
    iconColor: 'text-gray-500',
    titleColor: 'text-gray-900',
    msgBg: 'bg-gray-50',
    msgBorder: 'border-gray-200',
    msgText: 'text-gray-700',
    accentRing: 'focus:ring-blue-500',
    topStripe: 'bg-gradient-to-r from-gray-300 to-gray-400',
  },
};

/**
 * General-purpose dialog with copiable message.
 *
 * @param {object} props
 * @param {string} props.title - Dialog title.
 * @param {string} props.message - Message to display (copiable).
 * @param {'success'|'failure'|'neutral'} [props.vibe='neutral']
 * @param {Function} props.onClose
 * @param {string} [props.className]
 */
export default function MessageDialog({ title, message, vibe = 'neutral', onClose, className = '' }) {
  const [copied, setCopied] = useState(false);
  const v = VIBE_CONFIG[vibe] || VIBE_CONFIG.neutral;
  const VibeIcon = v.icon;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity"
        aria-hidden="true"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={`relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl ${v.border} border ${className}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="msg-dialog-title"
        >
          <div className={`h-1.5 ${v.topStripe}`} />

          <div className="p-6">
            <div className="absolute top-4 right-4">
              <button
                onClick={onClose}
                type="button"
                className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400"
                aria-label="Close dialog"
              >
                <CloseButtonIcon />
              </button>
            </div>

            <div className={`mx-auto mb-3 w-fit ${v.iconColor}`}>
              <VibeIcon />
            </div>

            <h3 id="msg-dialog-title" className={`text-center text-xl font-bold ${v.titleColor}`}>
              {title}
            </h3>

            <div className={`mt-4 flex items-start gap-2 rounded-lg border ${v.msgBorder} ${v.msgBg} px-3 py-2.5`}>
              <span className={`flex-1 text-sm ${v.msgText} select-all break-words`}>
                {message}
              </span>
              <button
                onClick={handleCopy}
                type="button"
                className={`flex-shrink-0 p-1.5 rounded-md transition-colors focus:outline-none focus:ring-2 ${v.accentRing} ${
                  copied
                    ? 'text-green-600 bg-green-50'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                }`}
                aria-label="Copy message"
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
              </button>
            </div>

            <p className={`mt-2 text-center text-xs transition-opacity ${copied ? 'text-green-600 opacity-100' : 'opacity-0'}`}>
              Copied to clipboard!
            </p>
          </div>
        </div>
      </div>
    </>
  );
}